import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Flex,
  LoadingOverlay,
  Modal,
  Text,
} from "@mantine/core";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";

import { columns } from "./columns";
import { EditCompanyModal } from "./edit-company-modal";
import { useUserContext } from "@/context/user.context";
import {
  useCompanyManageListQuery,
  useCreateCompanyManageMutation,
  useDeactivateCompanyMutation,
  useReactivateCompanyMutation,
} from "@/hooks/queries/company.queries";
import type {
  CompanyManagementTableRow,
  DraftCompany,
  ExistingCompanyRow,
} from "@/types/company-manage";
import type { Nullable } from "@/types/utils";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

const NOTIFICATION_MESSAGES = {
  addError: "Failed to add company. Please try again.",
  addSuccess: "Company added",
  deactivateError: "Failed to deactivate company. Please try again.",
  deactivateSuccess: "Company deactivated",
  reactivateError: "Failed to reactivate company. Please try again.",
  reactivateSuccess: "Company reactivated",
} as const;

interface CompanyManagementModalProps {
  onClose: () => void;
  opened: boolean;
}

export const CompanyManagementModal = ({
  onClose,
  opened,
}: CompanyManagementModalProps) => {
  const { company } = useUserContext();
  const { data: companies, isLoading } = useCompanyManageListQuery({
    enabled: opened,
  });

  const [draftCompany, setDraftCompany] =
    useState<Nullable<DraftCompany>>(null);
  const [editCompanyId, setEditCompanyId] = useState<Nullable<string>>(null);

  const { mutate: createCompany, isPending: isAddPending } =
    useCreateCompanyManageMutation();
  const { mutate: deactivateCompany } = useDeactivateCompanyMutation();
  const { mutate: reactivateCompany } = useReactivateCompanyMutation();

  const isOperationInProgress = draftCompany !== null || editCompanyId !== null;

  const addDraftCompany = useCallback(() => {
    setDraftCompany({
      isDraft: true,
      name: "",
      tempId: nanoid(),
    });
  }, []);

  const handleClose = useCallback(() => {
    setDraftCompany(null);
    setEditCompanyId(null);
    onClose();
  }, [onClose]);

  const handleAdd = useCallback(
    (name: string) => {
      createCompany(name, {
        onSuccess: () => {
          setDraftCompany(null);
          showUpwageNotification(
            NOTIFICATION_MESSAGES.addSuccess,
            NotificationTypes.Success,
          );
        },
        onError: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.addError,
            NotificationTypes.Error,
          );
        },
      });
    },
    [createCompany],
  );

  const handleCancelAdd = useCallback(() => {
    setDraftCompany(null);
  }, []);

  const handleEdit = useCallback((companyId: string) => {
    setEditCompanyId(companyId);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditCompanyId(null);
  }, []);

  const handleDeactivate = useCallback(
    (companyId: string) => {
      deactivateCompany(companyId, {
        onSuccess: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.deactivateSuccess,
            NotificationTypes.Success,
          );
        },
        onError: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.deactivateError,
            NotificationTypes.Error,
          );
        },
      });
    },
    [deactivateCompany],
  );

  const handleReactivate = useCallback(
    (companyId: string) => {
      reactivateCompany(companyId, {
        onSuccess: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.reactivateSuccess,
            NotificationTypes.Success,
          );
        },
        onError: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.reactivateError,
            NotificationTypes.Error,
          );
        },
      });
    },
    [reactivateCompany],
  );

  const tableData: CompanyManagementTableRow[] = useMemo(() => {
    const existingRows: ExistingCompanyRow[] = (companies ?? []).map((c) => ({
      ...c,
      isDraft: false as const,
    }));

    const activeCompanies = existingRows.filter((c) => !c.isDeleted);
    const inactiveCompanies = existingRows.filter((c) => c.isDeleted);
    const drafts: CompanyManagementTableRow[] = draftCompany
      ? [draftCompany]
      : [];

    return [...drafts, ...activeCompanies, ...inactiveCompanies];
  }, [companies, draftCompany]);

  const table = useReactTable({
    autoResetPageIndex: false,
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => (row.isDraft ? row.tempId : row.id),
    meta: {
      companyManagement: {
        activeCompanyId: company?.id ?? null,
        isAddPending,
        isOperationInProgress,
        onAdd: handleAdd,
        onCancelAdd: handleCancelAdd,
        onDeactivate: handleDeactivate,
        onEdit: handleEdit,
        onReactivate: handleReactivate,
        onUpdateDraft: (name: string) => {
          setDraftCompany((prev) => (prev ? { ...prev, name } : null));
        },
      },
    },
  });

  return (
    <>
      <Modal
        className="shadow-[0px_0px_24px_0px_rgba(0,0,0,0.1)] overflow-hidden"
        closeOnEscape={editCompanyId === null}
        onClose={handleClose}
        opened={opened}
        padding={0}
        radius={6}
        size="auto"
        withCloseButton={false}
      >
        <LoadingOverlay visible={isLoading} />

        <Flex
          className="border-b border-uw-gray-0 px-6 py-4"
          justify="space-between"
          align="center"
        >
          <Text fw={600} size="md" c="gray.9">
            Manage companies
          </Text>
          <Flex align="center" gap={24}>
            <Button
              className="h-10 px-4 gap-1 text-sm font-semibold"
              color="purple.5"
              disabled={isOperationInProgress}
              onClick={addDraftCompany}
              radius={4}
              rightSection={<FontAwesomeIcon icon={faPlus} size="xs" />}
            >
              Add company
            </Button>
            <ActionIcon onClick={handleClose} variant="transparent" size="sm">
              <FontAwesomeIcon
                className="bg-uw-gray-7 rounded-full text-uw-gray-0"
                icon={faCircleX}
              />
            </ActionIcon>
          </Flex>
        </Flex>

        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-uw-gray-0 bg-white"
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 first:pl-6 text-left uppercase"
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        </table>

        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full border-collapse">
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-uw-gray-0 bg-white">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-6 py-3 first:pl-6"
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Flex className="bg-white px-6 py-4">
          <Button
            className="h-8 px-4 text-sm font-semibold"
            onClick={handleClose}
            color="gray.0"
            c="gray.9"
            radius={4}
          >
            Close
          </Button>
        </Flex>
      </Modal>

      <EditCompanyModal
        companies={companies ?? []}
        companyId={editCompanyId}
        onClose={handleCloseEdit}
        opened={editCompanyId !== null}
      />
    </>
  );
};
